import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "src/store/hooks";
import HeaderActionBar from "../HeaderActionBar/HeaderActionBar";
import AddFlowchartButtonDropdown from "../AddFlowchartButtonDropdown/AddFlowchartButtonDropdown";
import {ReactFlowProvider} from "@xyflow/react";
import DiscardModal from "src/components/Modal/DiscardModal/DiscardModal";
import {estateFlowchartApiClient, ResponseStatus} from "src/EstateFlowchart/client/EstateFlowchartApiClient";
import {
    EstateFlowchartState,
    EstateItemObject,
    EstateToggle,
    FlowchartEstateType,
    ModalSource
} from "src/EstateFlowchart/model/EstateFlowchartTypes";

import {selectApprovedProfile} from "../../../ClientManagement/ClientProfile/activeProfileSlice";
import {Button, Dropdown, DropdownItem, Icon, PresentationPaneHeader} from "../../../components";
import useProfileEditableState from "../../../hooks/useProfileEditableState";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";
import {EstateItemType} from "../../forms/EstateItemType";
import {
    deleteEstateFlowchart,
    estateToggleSelector,
    getEstateFlowchartsSelector,
    selectedFlowchartIdSelector,
    setEstateToggle,
    setGenericErrorModal,
    setSelectedFlowchartId,
    setSelectedFlowchartIdAfterResourcesNav,
    setSideDrawerOpen
} from "../../slices/EstateFlowChartSlice";
import AddFlowchartModal from "../FlowChartModals/AddFlowchartModal";
import FlowchartVisualMain from "../FlowchartVisual/FlowchartVisualMain";
import SideDrawer from "../SideDrawer/SideDrawer";
import ScrollableTabs from "../ScrollableTabs/ScrollableTabs";
import {DropdownGroup} from "xps-react";
import {useAnalyticsTrackers} from "../../../utils/useAnalyticsTrackers";

type EstateFlowchartProps = {
    profileId: string,
    selectedFlowchartId?: string,
    estateName?: string
    isInReportSplitView?: boolean,
    width?: number,
    selectedCardId?: string,
    selectedCardRef?: React.RefObject<HTMLDivElement>,
}

const EstateFlowchart: React.FC<EstateFlowchartProps> = ({
                                                             profileId,
                                                             selectedFlowchartId,
                                                             estateName,
                                                             isInReportSplitView,
                                                             width,
                                                             selectedCardId,
                                                             selectedCardRef
                                                         }) => {
    const dispatch = useAppDispatch();
    const history = useHistory();

    const approvedProfile = useAppSelector(selectApprovedProfile);
    const estateFlowcharts = useAppSelector(getEstateFlowchartsSelector);
    const flowchartId = useAppSelector(selectedFlowchartIdSelector);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const estateToggleRedux = useAppSelector(estateToggleSelector);
    const resolvedFlowchartId = flowchartId ?? selectedFlowchartId ?? estateFlowcharts[0]?.flowchartId;
    const [showAddFlowchartModal, setShowAddFlowchartModal] = useState(false);
    const [deleteFlowchartModal, setDeleteFlowchartModal] = useState(false);
    const [flowchartIdToDelete, setFlowchartIdToDelete] = useState<string | null>(null);
    const [duplicateFlowchartModal, setDuplicateFlowchartModal] = useState(false);
    const [flowchartIdToDuplicate, setFlowchartIdToDuplicate] = useState<string | undefined>(undefined);
    const [estateToggleLocal, setEstateToggleLocal] = useState<string | undefined>(undefined);
    const {page} = useAnalyticsTrackers();

    useEffect(() => {
        if (estateFlowcharts?.length > 0) {
            if (releaseToggles?.enableEstateFlowchartMVPRelease) {
                dispatch(setSelectedFlowchartIdAfterResourcesNav(undefined));
            }
            if (!flowchartId && selectedFlowchartId === undefined) {
                dispatch(setSelectedFlowchartId(estateFlowcharts[0].flowchartId));
            } else if (selectedFlowchartId) {
                dispatch(setSelectedFlowchartId(selectedFlowchartId));
            }
            const estateToggle = estateFlowcharts.find(fc => fc.flowchartId === flowchartId)?.estateToggle ?? EstateToggle.FULL_ESTATE;
            if (estateToggleRedux !== estateToggle) {
                dispatch(setEstateToggle({toggle: estateToggle}));
            }

            dispatch(setSideDrawerOpen(false));
        }
    }, [profileId, estateFlowcharts, flowchartId]);

    useEffect(() => {
        if (estateName !== undefined) {
            switch (estateName) {
                case EstateToggle.OUT_OF_ESTATE:
                    setEstateToggleLocal(FlowchartEstateType.OUT_OF_ESTATE);
                    break;
                case EstateToggle.IN_ESTATE:
                    setEstateToggleLocal(FlowchartEstateType.IN_ESTATE);
                    break;
                default:
                    setEstateToggleLocal(FlowchartEstateType.FULL_ESTATE);
            }
        }
    }, []);

    const setCommunicationError = (errorStatus: ResponseStatus) => {
        dispatch(setGenericErrorModal({
            isOpen: true,
            operationId: errorStatus.traceId
        }));
    }

    const onEdit = (estateItemObject: EstateItemObject) => {
        if (!resolvedFlowchartId) {
            return;
        }
        if (estateItemObject.type === EstateItemType.REVOCABLE_TRUST) {
            history.push(`/Profile/${profileId}/ClientProfile/EstateFlowchart/${resolvedFlowchartId}/RevocableTrust/${estateItemObject.id}/BasicInformation`);
        } else if (estateItemObject.type === EstateItemType.IRREVOCABLE_TRUST) {
            history.push(`/Profile/${profileId}/ClientProfile/EstateFlowchart/${resolvedFlowchartId}/IrrevocableTrust/${estateItemObject.id}/BasicInformation`);
        } else if (estateItemObject.type === EstateItemType.WILL) {
            history.push(`/Profile/${profileId}/ClientProfile/EstateFlowchart/${resolvedFlowchartId}/Will/${estateItemObject.id}/BasicInformation`);
        } else if (estateItemObject.type === EstateItemType.MISCELLANEOUS) {
            history.push(`/Profile/${profileId}/ClientProfile/EstateFlowchart/${resolvedFlowchartId}/Miscellaneous/${estateItemObject.id}/BasicInformation`);
        }
    }

    const onReviewFiduciaries = () => {
        history.push(`/Profile/${profileId}/ClientProfile/EstateFlowchart/ReviewFiduciaries`);
        page('DEF_Click_Review_Fiduciaries')
    }

    const onAddFlowchart = () => {
        setShowAddFlowchartModal(true);
    }

    const selectFlowchartTab = (estateFlowchart: EstateFlowchartState) => {
        return flowchartId === estateFlowchart.flowchartId
            && `selected-flowchart ${releaseToggles?.enableEstateFlowchartMVPRelease ? '' : 'selected-flowchart-padding'}`;
    }

    const handleDeleteFlowchart = (flowchartId: string) => {
        estateFlowchartApiClient.deleteFlowchart(profileId, flowchartId)
            .then((res) => {
                if (!res.isSuccessStatusCode) {
                    if (releaseToggles?.enableEstateFlowchartMVPRelease) {
                        setCommunicationError(res);
                    }
                } else {
                    dispatch(deleteEstateFlowchart(flowchartId));
                }
            })
    };

    const {isProfileWithProposalsOrArchived} = useProfileEditableState();

    const getSelectedFlowchartId = (flowchartId: string, selectedFlowchartId?: string): string => {
        return selectedFlowchartId ?? flowchartId;
    };

    return (
        <>
            <AddFlowchartModal isOpen={showAddFlowchartModal}
                               modalSource={ModalSource.AddFlowchart}
                               handleCancel={() => setShowAddFlowchartModal(false)}/>

            <DiscardModal
                isOpen={deleteFlowchartModal}
                onClickDiscardChanges={() => {
                    if (flowchartIdToDelete) {
                        handleDeleteFlowchart(flowchartIdToDelete);
                    }
                    setDeleteFlowchartModal(false);
                    setFlowchartIdToDelete(null);
                }}
                onClickKeepEditing={() => {
                    setDeleteFlowchartModal(false);
                    setFlowchartIdToDelete(null);
                }}
                isEditing
                message="This flowchart will be deleted permanently."
                headerText="Delete Flowchart?"
                keepEditingText="Cancel"
                discardChangesText="Delete"
            />

            <AddFlowchartModal isOpen={duplicateFlowchartModal}
                               modalSource={ModalSource.DuplicateFlowchart}
                               handleCancel={() => setDuplicateFlowchartModal(false)} flowchartIdToDuplicate={flowchartIdToDuplicate}/>

            <div data-testid={estateName ? `estate-flowchart-page-${estateName}` : `estate-flowchart-page-${estateToggleRedux}`}
                 className={`estate-flowchart ${(releaseToggles?.enableEstateFlowchartMVPRelease && estateFlowcharts.length > 0) ? 'nt-background-color-estate-flowchart' : ''}`}>
                <SideDrawer/>

                <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
                    {selectedFlowchartId === undefined && <PresentationPaneHeader
                        displayName={approvedProfile.displayName}
                        title="Estate Flowchart"
                        titleRow={
                            <div data-testid="estate-flowchart-header-buttons" className="header-button-panel">
                                <Button
                                    className={'review-fiduciaries-button-header'}
                                    onClick={() => onReviewFiduciaries()}
                                    rounded={true}
                                    kind={'secondary'}
                                >
                                    REVIEW FIDUCIARIES
                                </Button>
                                <AddFlowchartButtonDropdown onClickFn={onAddFlowchart}/>
                            </div>
                        }
                    >
                    </PresentationPaneHeader>}
                    {
                        estateFlowcharts.length > 0 && selectedFlowchartId &&
                        <>
                            <PresentationPaneHeader
                                displayName={"ESTATE FLOWCHART"}
                                callingFromDEFReport={true}
                                title={`${estateFlowcharts.find(ele => ele.flowchartId?.trim() === selectedFlowchartId.trim())!.flowchartName} | ${estateName}`}
                            />
                            <HeaderActionBar onEdit={onEdit} flowcharts={estateFlowcharts}
                                             selectedFlowchartId={selectedFlowchartId}
                                             estateToggleLocal={estateToggleLocal}/>
                        </>
                    }


                    {estateFlowcharts?.length === 0 &&
                        <section data-testid="estate-flowchart-content" className={"estate-flowchart-main"}>
                            <h2 className={"add-flowchart-empty-state-text"}>Begin by adding a flowchart</h2>
                            <AddFlowchartButtonDropdown onClickFn={onAddFlowchart}/>
                        </section>
                    }
                    {estateFlowcharts?.length > 0 && (
                        <>
                            {selectedFlowchartId === undefined && <>
                                <div className={"presentation-pane-header presentation-pane-header-nav"}>
                                    <ScrollableTabs
                                        enableScroll={releaseToggles?.enableEstateFlowchartMVPRelease || false}
                                        numberOfTabs={estateFlowcharts.length}
                                        additionalMarginPerTab={16}
                                    >
                                        {estateFlowcharts.map((estateFlowchart) => (
                                            <div key={estateFlowchart.flowchartId}
                                                 data-testid={`flowchartDisplayName-${estateFlowchart?.flowchartName}`}
                                                 className={`display-name ${selectFlowchartTab(estateFlowchart)} ${releaseToggles?.enableEstateFlowchartMVPRelease && 'flowchart-title-style'} `}>
                                                <div className={"presentation-pane-display-name-text"}
                                                     data-testid={`flowchartTab-${estateFlowchart?.flowchartName}`}
                                                >
                                                    <button
                                                        onClick={() => {
                                                            dispatch(setSelectedFlowchartId(estateFlowchart.flowchartId));
                                                        }}
                                                    >
                                                        {estateFlowchart.flowchartName}
                                                    </button>
                                                </div>
                                                <Dropdown buttonKind={"secondary"}
                                                          data-testid="flowchart-action-menu"
                                                          className="flowchart-action-menu"
                                                          iconNameOpen={"menu_dots"}
                                                          iconNameClose={"menu_dots"}
                                                          buttonSize={"extra-small"}
                                                          buttonIcon={"only"}
                                                          dropdownKind={"menu"}
                                                          rounded={true}
                                                          inverse={true}
                                                          panelWidth={"auto"}
                                                          dynamicDropAlign={true}
                                                >
                                                    {releaseToggles?.enableEstateFlowchartMVPRelease &&
                                                        <DropdownGroup className="flowchart-header-dropdown-group"
                                                                       groupName="Duplicate">
                                                            <DropdownItem itemText="" value=""
                                                                          disabled={isProfileWithProposalsOrArchived}
                                                                          onClick={() => {

                                                                setFlowchartIdToDuplicate(estateFlowchart.flowchartId ? estateFlowchart.flowchartId : undefined);
                                                                setDuplicateFlowchartModal(true);
                                                                          }}>
                                                                <div className="flowchart-header-dropdown-item">
                                                                    Duplicate Flowchart
                                                                </div>
                                                            </DropdownItem>
                                                        </DropdownGroup>}
                                                    <DropdownGroup className="flowchart-header-dropdown-group"
                                                                   groupName="Delete">
                                                        <DropdownItem
                                                            disabled={isProfileWithProposalsOrArchived}
                                                            onClick={() => {
                                                                setFlowchartIdToDelete(estateFlowchart.flowchartId ? estateFlowchart.flowchartId : null);
                                                                setDeleteFlowchartModal(true);
                                                            }}>
                                                            <div className="dropdown__item-delete">
                                                                Delete Flowchart
                                                                <Icon name='delete' size='small'
                                                                      className='icon-delete'/>
                                                            </div>
                                                        </DropdownItem>
                                                    </DropdownGroup>
                                                </Dropdown>
                                            </div>
                                        ))}
                                    </ScrollableTabs>
                                </div>
                                <HeaderActionBar onEdit={onEdit}/>
                            </>}

                            <ReactFlowProvider>
                                {estateToggleLocal && releaseToggles?.enableEstateFlowchartMVPRelease
                                    ? (
                                        <FlowchartVisualMain
                                            flowcharts={estateFlowcharts}
                                            flowchartId={getSelectedFlowchartId(flowchartId!, selectedFlowchartId)}
                                            estateType={estateToggleLocal as FlowchartEstateType}
                                            isInReportSplitView={isInReportSplitView}
                                            width={width}
                                            selectedCardId={selectedCardId}
                                            selectedCardRef={selectedCardRef}
                                        />
                                    )
                                    : (
                                        <>
                                            {estateToggleRedux === EstateToggle.FULL_ESTATE && (
                                            <FlowchartVisualMain
                                                flowcharts={estateFlowcharts}
                                                flowchartId={getSelectedFlowchartId(flowchartId!, selectedFlowchartId)}
                                                estateType={FlowchartEstateType.FULL_ESTATE}
                                                isInReportSplitView={isInReportSplitView}
                                                width={width}
                                                selectedCardId={selectedCardId}
                                                selectedCardRef={selectedCardRef}
                                            />
                                            )}
                                            {estateToggleRedux === EstateToggle.IN_ESTATE && (
                                                <FlowchartVisualMain
                                                    flowcharts={estateFlowcharts}
                                                    flowchartId={getSelectedFlowchartId(flowchartId!, selectedFlowchartId)}
                                                    estateType={FlowchartEstateType.IN_ESTATE}
                                                    isInReportSplitView={isInReportSplitView}
                                                    width={width}
                                                    selectedCardId={selectedCardId}
                                                    selectedCardRef={selectedCardRef}
                                                />
                                            )}
                                            {estateToggleRedux === EstateToggle.OUT_OF_ESTATE && (
                                                <FlowchartVisualMain
                                                    flowcharts={estateFlowcharts}
                                                    flowchartId={getSelectedFlowchartId(flowchartId!, selectedFlowchartId)}
                                                    estateType={FlowchartEstateType.OUT_OF_ESTATE}
                                                    isInReportSplitView={isInReportSplitView}
                                                    width={width}
                                                    selectedCardId={selectedCardId}
                                                    selectedCardRef={selectedCardRef}
                                                />
                                            )}
                                        </>
                                    )
                                }
                            </ReactFlowProvider>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default EstateFlowchart;